import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import Lightbox from "../components/Lightbox";
import { GALLERY, GALLERY_FILTERS } from "../lib/constants";
import { usePageTitle } from "../lib/seo";

export default function Gallery() {
  usePageTitle("Gallery");
  const [filter, setFilter] = useState("all");
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const items = useMemo(
    () => (filter === "all" ? GALLERY : GALLERY.filter((g) => g.cat === filter)),
    [filter]
  );

  return (
    <div>
      <PageHero
        eyebrow="Our Gallery"
        title="A Look at Our Setups"
        sub="Wedding tents, decorated stages, pandals, lighting and event arrangements from our work in and around Bilaspur."
        image={GALLERY[0].src}
      />

      <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8 md:py-24">
        <div className="flex flex-wrap gap-3" data-testid="gallery-filter-bar">
          {GALLERY_FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              data-testid={`gallery-filter-${f.key}`}
              className={`rounded-full border px-5 py-2 text-xs font-semibold uppercase tracking-[0.15em] transition-all duration-300 ${
                filter === f.key
                  ? "border-gold bg-gradient-to-r from-[#BF953F] via-[#F3E5AB] to-[#AA771C] text-[#14120E]"
                  : "border-gold/25 text-stone-300 hover:border-gold/60 hover:text-amber-200"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="mt-10 columns-2 gap-4 space-y-4 md:columns-3" data-testid="gallery-grid">
          {items.map((g, i) => (
            <button
              key={`${filter}-${g.src}`}
              onClick={() => setLightboxIndex(i)}
              data-testid={`gallery-item-${i}`}
              className="group relative block w-full overflow-hidden rounded-xl border border-gold/15 transition-all duration-500 hover:border-gold/50"
            >
              <img src={g.src} alt={g.title} loading="lazy" className="w-full object-cover transition-transform duration-700 group-hover:scale-108" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
              <p className="absolute bottom-3 left-4 text-left font-display text-lg text-cream opacity-0 transition-all duration-500 group-hover:opacity-100">
                {g.title}
              </p>
            </button>
          ))}
        </div>

        <Reveal className="mt-16">
          <div className="card-royal flex flex-col items-center justify-between gap-6 p-8 text-center sm:p-10 md:flex-row md:text-left">
            <div>
              <h3 className="font-display text-2xl text-amber-100 sm:text-3xl">
                Planning something special at your venue?
              </h3>
              <p className="mt-2 text-sm text-stone-400">
                Tell us the occasion and the space — we will design a decor layout that fits it perfectly.
              </p>
            </div>
            <Link to="/contact" data-testid="gallery-cta-btn" className="btn-gold shrink-0">
              Request a Decor Layout <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>
      </section>

      {lightboxIndex !== null && (
        <Lightbox items={items} index={lightboxIndex} onClose={() => setLightboxIndex(null)} onNav={setLightboxIndex} />
      )}
    </div>
  );
}
